import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, MessageCircle, Calendar, Sparkles, Play } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import MarkdownContent from "../components/MarkdownContent";
import { trackEvent } from "../lib/analytics";
import { resolveImageUrl } from "../components/ImageUpload";
import { useRefreshOnResume } from "../hooks/useRefreshOnResume";
import { useTablet } from "../hooks/useTablet";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const WA = "https://wa.me/77018017755";

// Mirrors backend LEVEL_CASHBACK_PCT in /app/backend/bonuses.py
const LEVEL_CASHBACK = { Diamond: 4, Platinum: 3, Gold: 2, Silver: 1, Standard: 1 };
import { useSeo } from "../lib/seo";

export default function ProductDetail() {
  const { tablet, width } = useTablet();
  const wide = width >= 1024;
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, tField } = useLanguage();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [balance, setBalance] = useState(null);
  const touchStartX = useRef(null);
  const videoRef = useRef(null);

  useSeo(product ? {
    title: `${`${product.name}${product.brand ? " — " + String(product.brand).trim() : ""}`.slice(0, 52).replace(/[\s,.;—-]+$/, "")} | ANGIOLINI, Алматы`,
    description: (product.description || `${product.name}. Цена ${Number(product.price || 0).toLocaleString("ru-RU")} ₸. Бутик ANGIOLINI в Алматы.`).replace(/\s+/g, " ").slice(0, 300),
    canonical: `/app/catalog/${product.id}`,
    image: (product.images || [])[0],
  } : {});

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(r => {
      setProduct(r.data);
      // Fire product_view event once the product is loaded (best effort)
      trackEvent("product_view", { target_id: r.data.id, target_name: r.data.name, page: `/app/catalog/${id}` });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  // Возврат в приложение из фона — обновляем цену, размеры и наличие товара.
  useRefreshOnResume(useCallback(() => {
    axios.get(`${API}/products/${id}`).then(r => setProduct(r.data)).catch(() => {});
  }, [id]));

  // Fetch the client's current VIP level — backend awards cashback using the
  // tier they had BEFORE this purchase, so we display that exact % here.
  useEffect(() => {
    if (!user || user.role === "admin") return;
    axios.get(`${API}/bonus/balance`, { withCredentials: true })
      .then(r => setBalance(r.data))
      .catch(() => {});
  }, [user]);

  // Combined media list (computed early so we can wire up navigation hooks).
  // Videos come FIRST so the first slide autoplays and immediately grabs
  // attention; images follow.
  const media = product ? [
    ...(product.videos || []).map(v => ({ kind: "video", src: v.url, poster: v.poster, duration: v.duration })),
    ...(product.review_videos || []).map(v => ({ kind: "video", src: v.url, poster: v.poster, duration: v.duration, review: true })),
    ...(product.images || []).map(img => ({ kind: "image", src: img })),
  ] : [];

  const goPrev = useCallback(() => {
    if (media.length < 2) return;
    setImgIdx(i => (i - 1 + media.length) % media.length);
  }, [media.length]);
  const goNext = useCallback(() => {
    if (media.length < 2) return;
    setImgIdx(i => (i + 1) % media.length);
  }, [media.length]);

  // Keyboard arrows navigation (desktop).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext]);

  // Autoplay video whenever it becomes the current slide.
  const current = media[imgIdx] || media[0];
  useEffect(() => {
    if (current?.kind === "video" && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [imgIdx, current]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goNext(); else goPrev();
    }
  };

  if (loading) return <div style={{ background: "var(--ang-bg)", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ang-text-faint)", fontFamily: "Manrope" }}>{t.product.loading}</div>;
  if (!product) return <div style={{ background: "var(--ang-bg)", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ang-text-faint)", fontFamily: "Manrope", flexDirection: "column", gap: "16px" }}>
    <p>{t.product.notFound}</p>
    <button onClick={() => navigate("/app/catalog")} style={{ color: "var(--ang-gold)", background: "none", border: "none", cursor: "pointer", fontFamily: "Manrope" }}>← {t.product.goBack}</button>
  </div>;

  const waMsg = `Здравствуйте! Интересует товар: ${product.name}, цена ${product.price.toLocaleString("ru-RU")} ₸. ${selectedSize ? `Размер: ${selectedSize}.` : ""}`;
  const waUrl = `${WA}?text=${encodeURIComponent(waMsg)}`;

  return (
    <div style={{ background: "var(--ang-bg)", minHeight: "100vh" }}>
      {/* Media */}
      <div
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ position: "relative", width: wide ? "46%" : "100%", float: wide ? "left" : "none",
          marginRight: wide ? "32px" : 0, aspectRatio: "9/16", maxHeight: wide ? "720px" : "640px",
          background: "var(--ang-bg)", overflow: "hidden", touchAction: "pan-y", borderRadius: wide ? "10px" : 0 }}
        data-testid="product-media-carousel"
      >
        {current?.kind === "video" ? (
          <video
            key={current.src}
            ref={videoRef}
            src={resolveImageUrl(current.src)}
            poster={current.poster ? resolveImageUrl(current.poster) : undefined}
            controls
            autoPlay
            muted
            playsInline
            preload="metadata"
            data-testid="product-video-player"
            style={{ width: "100%", height: "100%", objectFit: "cover", background: "var(--ang-bg)" }}
          />
        ) : (
          <img src={resolveImageUrl(current?.src) || resolveImageUrl(product.images?.[0])} alt={`${tField(product, "name")}${product.brand ? " — " + String(product.brand).trim() : ""}${product.category ? ", " + product.category : ""}`}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            draggable={false}
            onError={e => { e.target.src = "https://via.placeholder.com/360x640/1A1A1A/C5A059?text=Angiolini"; }} />
        )}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 30%, transparent 60%, rgba(0,0,0,0.8) 100%)", pointerEvents: "none" }} />

        {/* Review-video badge */}
        {current?.kind === "video" && current?.review && (
          <div data-testid="review-video-badge"
               style={{ position: "absolute", top: "14px", left: "50%", transform: "translateX(-50%)",
                        background: "var(--ang-gold-strong)", color: "var(--ang-bg)", padding: "4px 12px",
                        borderRadius: "12px", fontFamily: "Manrope", fontSize: "10px", fontWeight: 700,
                        letterSpacing: "0.08em", textTransform: "uppercase", pointerEvents: "none", zIndex: 3 }}>
            Обзор от консультанта
          </div>
        )}

        {/* Back btn */}
        <button onClick={() => navigate(-1)} data-testid="back-btn"
          style={{ position: "absolute", top: "52px", left: "16px", background: "rgba(0,0,0,0.5)", border: "none", borderRadius: "50%", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(10px)", zIndex: 5 }}>
          <ChevronLeft size={20} style={{ color: "var(--ang-text)" }} />
        </button>

        {product.discount && (
          <div data-testid="product-discount-badge" style={{ position: "absolute", top: "52px", right: "16px", background: "#E11D2A", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "18px", fontWeight: 900, padding: "6px 14px", borderRadius: "4px", boxShadow: "0 4px 18px rgba(225,29,42,0.55)", border: "1px solid var(--ang-border-strong)", letterSpacing: "0.01em", zIndex: 5 }}>
            -{product.discount}%
          </div>
        )}

        {/* Prev / Next arrows (visible when multiple media). Semi-transparent,
            larger touch target for both desktop and mobile. */}
        {media.length > 1 && (
          <>
            <button onClick={goPrev} data-testid="media-prev-btn" aria-label="Previous slide"
              style={{ position: "absolute", top: "50%", left: "10px", transform: "translateY(-50%)", background: "rgba(0,0,0,0.4)", border: "1px solid var(--ang-border-strong)", borderRadius: "50%", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(10px)", zIndex: 6 }}>
              <ChevronLeft size={22} style={{ color: "var(--ang-text)" }} />
            </button>
            <button onClick={goNext} data-testid="media-next-btn" aria-label="Next slide"
              style={{ position: "absolute", top: "50%", right: "10px", transform: "translateY(-50%)", background: "rgba(0,0,0,0.4)", border: "1px solid var(--ang-border-strong)", borderRadius: "50%", width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(10px)", zIndex: 6 }}>
              <ChevronRight size={22} style={{ color: "var(--ang-text)" }} />
            </button>
          </>
        )}

        {/* Media selector dots */}
        {media.length > 1 && (
          <div style={{ position: "absolute", bottom: "16px", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "6px", zIndex: 5 }}>
            {media.map((m, i) => (
              <button key={i} onClick={() => setImgIdx(i)}
                      data-testid={`media-dot-${i}`}
                      style={{ width: i === imgIdx ? "20px" : (m.kind === "video" ? "12px" : "6px"),
                               height: "6px",
                               borderRadius: "3px",
                               background: i === imgIdx ? "var(--ang-gold)" : "var(--ang-text-faint)",
                               border: "none", cursor: "pointer",
                               transition: "all 0.3s",
                               display: "flex", alignItems: "center", justifyContent: "center" }}>
                {m.kind === "video" && i !== imgIdx && (
                  <Play size={6} fill="var(--ang-text-soft)" color="var(--ang-text-soft)" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Details */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        style={{ padding: "20px 20px 100px" }}>
        <div style={{ fontFamily: "Manrope", fontSize: "10px", color: "var(--ang-gold-strong)", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "8px" }}>{product.category}</div>
        <h1 data-testid="product-name" style={{ fontFamily: "Cormorant Garamond", fontSize: "28px", color: "var(--ang-text)", fontWeight: 400, lineHeight: 1.2, marginBottom: "16px" }}>{tField(product, "name")}</h1>

        {/* Price */}
        <div style={{ display: "flex", alignItems: "baseline", gap: "14px", marginBottom: "20px", flexWrap: "wrap" }}>
          {product.old_price && <span style={{ fontFamily: "Manrope", fontSize: "16px", color: "var(--ang-text-faint)", textDecoration: "line-through" }}>{(product.old_price).toLocaleString("ru-RU")} ₸</span>}
          <span data-testid="product-price" style={{ fontFamily: "Cormorant Garamond", fontSize: "34px", color: product.old_price ? "#E11D2A" : "var(--ang-gold)", fontWeight: 600 }}>{(product.price).toLocaleString("ru-RU")} ₸</span>
          {product.discount && <span style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text)", background: "#E11D2A", fontWeight: 800, padding: "3px 8px", borderRadius: "3px", letterSpacing: "0.04em" }}>−{product.discount}%</span>}
        </div>

        {/* Sizes */}
        {product.sizes?.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "10px" }}>{t.product.size}</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {product.sizes.map(s => (
                <button key={s} data-testid={`size-${s}`} onClick={() => setSelectedSize(s)}
                  style={{ width: "48px", height: "48px", border: `1px solid ${s === selectedSize ? "var(--ang-gold)" : "var(--ang-border)"}`, borderRadius: "4px", background: s === selectedSize ? "var(--ang-gold-soft)" : "transparent", color: s === selectedSize ? "var(--ang-gold)" : "var(--ang-text-soft)", fontFamily: "Manrope", fontSize: "13px", fontWeight: s === selectedSize ? 700 : 400, cursor: "pointer", transition: "all 0.2s" }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Состав / материал */}
        {product.material && (
          <div style={{ marginBottom: "20px" }} data-testid="product-material">
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "6px" }}>Состав</div>
            <div style={{ fontFamily: "Manrope", fontSize: "14px", color: "var(--ang-text-soft)" }}>{product.material}</div>
          </div>
        )}

        {/* Description */}
        {(tField(product, "description") || product.description) && (
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-faint)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "10px" }}>{t.product.description}</div>
            <MarkdownContent text={tField(product, "description") || product.description} style={{ color: "var(--ang-text-soft)", fontSize: "14px", lineHeight: 1.7 }} />
          </div>
        )}

        {/* Bonus info — uses the client's CURRENT VIP tier (the % applied at purchase time). */}
        {(() => {
          const level = balance?.vip_level || "Silver";
          const pct = LEVEL_CASHBACK[level] ?? 1;
          const bonus = Math.floor(product.price * pct / 100);
          // Logged-out fallback: keep neutral copy without misleading %.
          if (!user || user.role === "admin") {
            return (
              <div style={{ background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold-soft)", borderRadius: "6px", padding: "12px 16px", marginBottom: "14px" }}>
                <div data-testid="bonus-hint-guest" style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-gold)" }}>
                  Войдите, чтобы увидеть ваш персональный кэшбэк <strong>1–4 %</strong> с этой покупки
                </div>
              </div>
            );
          }
          return (
            <div style={{ background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold-soft)", borderRadius: "6px", padding: "12px 16px", marginBottom: "14px" }}>
              <div data-testid="bonus-hint" style={{ fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-gold)" }}>
                При покупке вы получите <strong>{bonus.toLocaleString("ru-RU")}</strong> бонусов
                <span data-testid="bonus-hint-rate" style={{ opacity: 0.75 }}>{" "}({pct}% · {level})</span>
              </div>
            </div>
          );
        })()}

        {/* Virtual try-on CTA */}
        <button data-testid="open-tryon-btn" onClick={() => navigate(`/app/tryon?product=${product.id}`)}
          style={{ width: "100%", background: "linear-gradient(135deg, var(--ang-gold-soft), var(--ang-gold-soft))",
            border: "1px solid var(--ang-gold-line)", borderRadius: "6px", padding: "14px 16px",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
            cursor: "pointer", marginBottom: "20px", color: "var(--ang-gold)",
            fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, letterSpacing: "0.08em",
            textTransform: "uppercase" }}>
          <Sparkles size={16} />
          Виртуальная примерка
        </button>
      </motion.div>

      <div style={{ clear: "both" }} />

      {/* Fixed bottom buttons */}
      <div style={{ position: "fixed", bottom: tablet ? "20px" : "64px", left: tablet ? "auto" : "50%",
        right: tablet ? "32px" : "auto", transform: tablet ? "none" : "translateX(-50%)",
        width: "100%", maxWidth: tablet ? "520px" : "430px", padding: "12px 16px",
        background: tablet ? "transparent" : "linear-gradient(to top, var(--ang-bg), rgba(10,10,10,0.95))", display: "flex", gap: "10px" }}>
        <button data-testid="appointment-btn" onClick={() => navigate("/app/appointment")}
          style={{ flex: 1, background: "var(--ang-surface)", border: "1px solid var(--ang-border)", borderRadius: "4px", padding: "14px", color: "var(--ang-text)", fontFamily: "Manrope", fontSize: "12px", fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", letterSpacing: "0.05em" }}>
          <Calendar size={16} style={{ color: "var(--ang-gold)" }} />{t.product.fitting}
        </button>
        <button data-testid="whatsapp-product-btn" onClick={() => { trackEvent("whatsapp_click", { page: `/app/catalog/${id}`, target_id: product.id, target_name: product.name }); window.open(waUrl, "_blank"); }}
          style={{ flex: 2, background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))", borderRadius: "4px", padding: "14px", color: "var(--ang-bg)", fontFamily: "Manrope", fontSize: "12px", fontWeight: 700, cursor: "pointer", border: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", letterSpacing: "0.08em" }}>
          <MessageCircle size={16} />{t.product.askWhatsapp}
        </button>
      </div>
    </div>
  );
}
